import { ArrowBackIos, ArrowForwardIos } from "@material-ui/icons";
import React, { memo, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import PuiTypography from "../../../components/elements/PuiTypography";
import { IsDevice } from "../../../components/function/useDeviceFlag";
import { useInitFlag } from "../../../components/function/useInitFlag";
import { useInitText } from "../../../components/function/useInitText";
import PuiMessage from "../../../components/widgets/PuiMessage";
import { AppState } from "../../../root/RootReducer";
import { Folder } from "../../models/Timeline";
import { GetDir, ResetDeleteError } from "../../store/actions/DirectoryActions";
import stylesPc from "./DirectoryIndex.module.css";
import stylesSp from "./DirectoryIndex_SP.module.css";

interface DirectoryPathProps {
    selectedHistoryPathList: { id: number; name: string }[];
    setSelectedhistoryPathList: React.Dispatch<
        React.SetStateAction<
            {
                id: number;
                name: string;
            }[]
        >
    >;
    directoryList: Folder[];
    selectedPathList: any[];
    setSelectedPathList: React.Dispatch<React.SetStateAction<any[]>>;
    setCurrentDirectory: React.Dispatch<
        React.SetStateAction<{
            id: number;
            name: string;
        }>
    >;
    currentDirectory: {
        id: number;
        name: string;
    };
    directoryRef: React.MutableRefObject<HTMLTableElement | null>;
}

function DirectoryPath({
    selectedHistoryPathList,
    setSelectedhistoryPathList,
    directoryList,
    selectedPathList,
    setSelectedPathList,
    setCurrentDirectory,
    currentDirectory,
    directoryRef,
}: DirectoryPathProps) {
    // レスポンシブ対応：ブラウザサイズによるCSSファイルの切り替え
    const styles = IsDevice("desktop") ? stylesPc : stylesSp;
    // Storeからデータ取得
    const { user, deleteError } = useSelector((state: AppState) => ({
        user: state.user.userInfo,
        deleteError: state.directory.deleteError,
    }));
    const dispatch = useDispatch();
    // フッテージ削除エラー表示フラグ
    const [isDeleteError, setIsDeleteError] = useInitFlag();
    const [deleteErrorMsg, setDeleteErrorMsg] = useInitText();

    // 履歴IDリスト
    const [directoryIdList, setDirectoryIdList] = useState<number[]>([]);

    // > ボタン押下時の表示するディレクトリID
    const [forwordDirId, setForwordDirId] = useState<number>();

    // > ボタンが押せるかの判断
    const [isForword, setIsForword] = useState<boolean>(false);

    // エラーメッセージを閉じる処理
    const closeDeleteError = () => {
        setIsDeleteError(false);
        dispatch(ResetDeleteError());
    };

    useEffect(() => {
        if (deleteError) {
            setIsDeleteError(true);
            setDeleteErrorMsg(deleteError);
        }
        // eslint-disable-next-line
    }, [deleteError]);

    // IDリストに値を格納処理
    useEffect(() => {
        const number: number[] = selectedHistoryPathList.map((path) => {
            return path.id;
        });
        setDirectoryIdList(number);
        // eslint-disable-next-line
    }, [selectedHistoryPathList.length]);

    // > ボタン押下時の表示するディレクトリIDを見つける処理
    useEffect(() => {
        if (!directoryList) return;
        const dirId = directoryIdList.reverse().find((id) => {
            let targetId;
            for (let i = 0; i < directoryList.length; i++) {
                if (id === directoryList[i].id) {
                    targetId = id;
                }
            }
            return targetId;
        });
        // > 押下出来るかの判断(IDが見つかったらtrue)
        if (dirId !== undefined) {
            setIsForword(true);
        } else {
            setIsForword(false);
        }
        setForwordDirId(dirId);
        // eslint-disable-next-line
    }, [directoryList]);

    // < ボタン押下時
    const BackDirectory = () => {
        if (selectedPathList.length > 1) {
            // 選択したIndexよりフォルダー名配列の値を切り替える
            const tempSelectPathList: any[] = selectedPathList.slice(
                0,
                selectedPathList.length === 1 ? 1 : selectedPathList.length - 1
            );
            // 値を格納
            const id = tempSelectPathList[tempSelectPathList.length - 1].id;
            const name = tempSelectPathList[tempSelectPathList.length - 1].name;
            // ディレクトリ一覧取得APIコール
            if (user) {
                dispatch(GetDir(id, Number(user.id)));
            }
            // 表示しているディレクトリ
            setCurrentDirectory({ id, name });
            // 履歴用の配列に保存
            setSelectedhistoryPathList([
                ...selectedHistoryPathList,
                {
                    id: id,
                    name: name,
                },
            ]);
            // 現在の配列
            setSelectedPathList(tempSelectPathList);
            if (!directoryRef || !directoryRef.current) return;
            directoryRef.current.scrollTop = 0;
        }
    };

    // > ボタン押下時
    const ForwordDirectory = () => {
        if (isForword) {
            // API呼び出し
            if (user && forwordDirId !== undefined) {
                dispatch(GetDir(forwordDirId, Number(user.id)));
            }

            // 表示するディレクトリを探す
            const currentIndex = selectedHistoryPathList.findIndex((dir) => {
                return dir.id === forwordDirId;
            });
            // 表示しているディレクトリ
            setCurrentDirectory({
                id: selectedHistoryPathList[currentIndex].id,
                name: selectedHistoryPathList[currentIndex].name,
            });
            // 現在のpath
            setSelectedPathList([
                ...selectedPathList,
                {
                    id: selectedHistoryPathList[currentIndex].id,
                    name: selectedHistoryPathList[currentIndex].name,
                },
            ]);

            // 履歴用のpathに保存
            setSelectedhistoryPathList([
                ...selectedHistoryPathList,
                {
                    id: selectedHistoryPathList[selectedHistoryPathList.length - 2].id,
                    name: selectedHistoryPathList[selectedHistoryPathList.length - 2].name,
                },
            ]);
            if (!directoryRef || !directoryRef.current) return;
            directoryRef.current.scrollTop = 0;
        }
    };
    return (
        <>
            <div className={styles.namePathDiv}>
                {selectedPathList && (
                    <div className={styles.currentDirectory}>
                        <ArrowBackIos
                            onClick={BackDirectory}
                            className={
                                selectedPathList.length > 1
                                    ? styles.arrowBack
                                    : styles.disableArrowBack
                            }
                        />
                        <ArrowForwardIos
                            onClick={ForwordDirectory}
                            className={isForword ? styles.arrowForward : styles.disableArrowForward}
                        />
                        <div className={styles.currentDirectoryName}>
                            <PuiTypography color="white">{currentDirectory.name}</PuiTypography>
                        </div>
                    </div>
                )}
            </div>
            <PuiMessage
                open={isDeleteError}
                type="error"
                title={deleteErrorMsg.split(",").join("\n")}
                duration={5000}
                onClose={closeDeleteError}
            />
        </>
    );
}
export default memo(DirectoryPath);
